import { t } from '@lingui/macro';
import { msg } from '@lingui/core/macro';
import React, { Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@shared/api/supabaseClient';
import { useAuth } from '@shared/hooks/useAuth';
import { useIsMobile } from '@shared/hooks/use-mobile';
import { apiClient } from '@shared/api/api-client';
import { useNotificationRealtime } from '@shared/hooks/notifications/useNotificationRealtime';
import {
  useApproveDoctorAccountControlRequest,
  useDenyDoctorAccountControlRequest,
  useDoctorAccountControlDoctorInbox,
  useDoctorAccountControlDoctorSummary,
  useEndDoctorAccountControl,
  useRevokeDoctorAccountControl,
} from '@shared/hooks/api/useDoctorAccountControl';
import { getUnreadPatientsCount } from '@features/doctor/pages/patients/services/patientsService';
import { GlobalMessagingRealtimeProvider } from '@features/messages/providers/GlobalMessagingRealtimeProvider';
import { CallsShellOverlay } from '@shared/calls';
import { DiagnosticRecorderOverlay } from '@shared/components/DiagnosticRecorderOverlay';
import { useMessagingRooms } from '@features/messages/hooks/useMessagingApi';
import { getNavItemsForRole, getNavItemByPath } from '@shared/lib/navigation';
import { queryClient, queryKeys } from '@shared/lib/react-query';
import { queryPersister } from '@shared/lib/react-query-persist';
import {
  clearDoctorControlStorage,
  useDoctorControlStore,
} from '@shared/stores/doctor-control-store';
import { useDoctorWorkspace } from '@shared/hooks/useDoctorWorkspace';
import { localizePath, stripLocaleFromPathname, useLocale } from '@shared/i18n';
import { Building, ShieldCheck } from 'lucide-react';
import { SidebarProvider, SidebarInset, useSidebar } from '@shared/ui/sidebar';
import { AppSidebar } from '@shared/layout/AppSidebar';
import { AppHeader } from '@shared/layout/AppHeader';
import { NetworkStatusBanner } from '@shared/layout/NetworkStatusBanner';
import { PatientMobileHeader } from '@shared/layout/PatientMobileHeader';
import { DoctorMobileHeader } from '@shared/layout/DoctorMobileHeader';
import { MobileBottomNav } from '@shared/layout/MobileBottomNav';
import {
  getMobileShellClassName,
  MOBILE_NAV_KEYS_BY_ROLE,
  shouldShowMobileBottomNav,
} from '@shared/layout/mobileNavConfig';

import { ErrorBoundary, LoadingSkeleton } from '@shared/components';
import { Alert, AlertDescription, AlertTitle } from '@shared/ui/alert';
import { Button } from '@shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@shared/ui/dialog';
import { Avatar, AvatarFallback } from '@shared/ui/avatar';
import { toast } from 'sonner';

interface AppShellProps {
  children?: React.ReactNode;
}

type DoctorControlRealtimePayload = {
  controlRequestId?: string;
  doctorId?: string;
  requesterId?: string;
  occurredAt?: string;
};

// Must render inside SidebarProvider so "More" can open the existing mobile sidebar sheet.
const StaffMobileNav: React.FC<
  Omit<React.ComponentProps<typeof MobileBottomNav>, 'onMore'>
> = props => {
  const { setOpenMobile } = useSidebar();
  return <MobileBottomNav {...props} onMore={() => setOpenMobile(true)} />;
};

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const {
    user,
    actorUser,
    authContext,
    isLoading,
    isAuthenticated,
    isDoctorControlMode,
    activeDoctorControlRequest,
    refetchProfile,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const isMobile = useIsMobile();
  const {
    effectivePortalRole,
    isEffectiveOrganizationOwner,
    isDelegatedDoctorWorkspace,
    normalizedPathname,
    resolveDoctorPath,
    returnPath,
    workspaceHomePath,
  } = useDoctorWorkspace();
  const clearDoctorControl = useDoctorControlStore(state => state.clearControl);
  const [doctorApprovalDialogOpen, setDoctorApprovalDialogOpen] = React.useState(false);
  const [remoteControlExitDialogReason, setRemoteControlExitDialogReason] = React.useState<
    'revoked' | 'ended' | null
  >(null);
  const lastPromptedRequestIdRef = React.useRef<string | null>(null);

  // Get current page info for breadcrumbs and title
  const baseNavItems = getNavItemsForRole(effectivePortalRole);
  const navItems = React.useMemo(() => {
    if (!user) return baseNavItems;
    let items = [...baseNavItems];

    const isOwner = effectivePortalRole === 'doctor' && isEffectiveOrganizationOwner;
    if (isOwner && !items.find(item => item.key === 'manage-organization')) {
      items = [
        ...items,
        {
          key: 'manage-organization',
          label: msg({
            id: 'shared.layout.AppShell.label-manage-organization',
            message: 'Manage Organization',
          }),
          path: '/organization/manage',
          icon: Building,
          roles: ['doctor'],
          description: msg({
            id: 'shared.layout.AppShell.description-organization-configuration-and-members',
            message: 'Organization configuration and members',
          }),
        },
      ];
    }

    return isDelegatedDoctorWorkspace
      ? items.map(item => ({
          ...item,
          path: resolveDoctorPath(item.path),
        }))
      : items;
  }, [
    baseNavItems,
    effectivePortalRole,
    isDelegatedDoctorWorkspace,
    isEffectiveOrganizationOwner,
    resolveDoctorPath,
    user,
  ]);

  const doctorPatientsUnreadQuery = useQuery({
    queryKey: queryKeys.doctor.patientsUnreadCount(),
    queryFn: getUnreadPatientsCount,
    enabled: Boolean(
      effectivePortalRole === 'doctor' && authContext && isAuthenticated && !isLoading
    ),
    staleTime: 30_000,
  });
  const messagingRoomsQuery = useMessagingRooms({
    enabled: Boolean(isAuthenticated && !isLoading && user?.id),
  });

  const doctorControlInboxQuery = useDoctorAccountControlDoctorInbox(
    user?.role === 'doctor' && !isDoctorControlMode
  );
  const adminDoctorControlSummaryQuery = useDoctorAccountControlDoctorSummary(
    activeDoctorControlRequest?.doctorId,
    (actorUser?.role === 'admin' || actorUser?.role === 'super_admin') &&
      activeDoctorControlRequest?.status === 'active' &&
      Boolean(activeDoctorControlRequest?.doctorId)
  );
  const approveDoctorControlMutation = useApproveDoctorAccountControlRequest();
  const denyDoctorControlMutation = useDenyDoctorAccountControlRequest();
  const revokeDoctorControlMutation = useRevokeDoctorAccountControl();
  const endDoctorControlMutation = useEndDoctorAccountControl();

  const pendingDoctorControlRequest = doctorControlInboxQuery.data?.pendingRequest ?? null;
  const activeDoctorControlSession = doctorControlInboxQuery.data?.activeSession ?? null;
  const isAdminInActiveControlMode =
    isDoctorControlMode &&
    (actorUser?.role === 'admin' || actorUser?.role === 'super_admin') &&
    activeDoctorControlRequest?.status === 'active';
  const shouldThrottleControlModeNetwork =
    isDelegatedDoctorWorkspace || (user?.role === 'doctor' && Boolean(activeDoctorControlSession));
  const lastRemoteExitRequestIdRef = React.useRef<string | null>(null);

  useNotificationRealtime({ enabled: Boolean(isAuthenticated && !isLoading && user?.id) });

  const exitRemoteDoctorControlSession = React.useCallback(
    async (reason: 'revoked' | 'ended') => {
      const requestId = activeDoctorControlRequest?.id;
      if (!requestId || lastRemoteExitRequestIdRef.current === requestId) {
        return;
      }

      lastRemoteExitRequestIdRef.current = requestId;
      clearDoctorControlStorage();
      queryClient.clear();
      apiClient.clearSessionBoundState(`doctor_control_${reason}`);

      try {
        await queryPersister.removeClient();
      } catch {
        // Best effort; runtime query cache has already been cleared.
      }

      await refetchProfile();

      setRemoteControlExitDialogReason(reason);
      navigate(returnPath, { replace: true });
    },
    [activeDoctorControlRequest?.id, navigate, refetchProfile, returnPath]
  );

  React.useEffect(() => {
    if (isLoading || !actorUser) {
      return;
    }

    if (actorUser?.role !== 'admin' && actorUser?.role !== 'super_admin') {
      clearDoctorControl();
    }
  }, [actorUser, actorUser?.role, clearDoctorControl, isLoading]);

  React.useEffect(() => {
    if (user?.role !== 'doctor') {
      return;
    }

    if (!pendingDoctorControlRequest?.id) {
      return;
    }

    if (lastPromptedRequestIdRef.current === pendingDoctorControlRequest.id) {
      return;
    }

    lastPromptedRequestIdRef.current = pendingDoctorControlRequest.id;
    setDoctorApprovalDialogOpen(true);
  }, [pendingDoctorControlRequest?.id, user?.role]);

  React.useEffect(() => {
    if (isLoading || !actorUser || activeDoctorControlRequest?.status !== 'active') {
      return;
    }

    if (actorUser?.role !== 'admin' && actorUser?.role !== 'super_admin') {
      clearDoctorControlStorage();
    }
  }, [activeDoctorControlRequest?.status, actorUser, actorUser?.role, isLoading]);

  React.useEffect(() => {
    if (
      !isAdminInActiveControlMode ||
      !activeDoctorControlRequest?.id ||
      !adminDoctorControlSummaryQuery.data
    ) {
      return;
    }

    if (adminDoctorControlSummaryQuery.data.activeSession?.id !== activeDoctorControlRequest.id) {
      void exitRemoteDoctorControlSession('revoked');
    }
  }, [
    activeDoctorControlRequest?.id,
    adminDoctorControlSummaryQuery.data,
    exitRemoteDoctorControlSession,
    isAdminInActiveControlMode,
  ]);

  React.useEffect(() => {
    if (!isAdminInActiveControlMode || !actorUser?.id || !activeDoctorControlRequest?.id) {
      return;
    }

    const topicIds = Array.from(
      new Set(
        [actorUser.id, activeDoctorControlRequest.requesterId].filter((value): value is string =>
          Boolean(value)
        )
      )
    );

    const handleRevoked = ({ payload }: { payload: unknown }) => {
      const event = payload as DoctorControlRealtimePayload;
      if (event?.controlRequestId !== activeDoctorControlRequest.id) {
        return;
      }
      void exitRemoteDoctorControlSession('revoked');
    };

    const handleEnded = ({ payload }: { payload: unknown }) => {
      const event = payload as DoctorControlRealtimePayload;
      if (event?.controlRequestId !== activeDoctorControlRequest.id) {
        return;
      }
      void exitRemoteDoctorControlSession('ended');
    };

    const channels = topicIds.map(topicId => {
      const channel = supabase.channel(`doctor-control:${topicId}`, {
        config: { private: true, broadcast: { ack: false } },
      });

      channel
        .on('broadcast', { event: 'doctor_control_revoked' }, handleRevoked)
        .on('broadcast', { event: 'doctor_control_ended' }, handleEnded)
        .subscribe();

      return channel;
    });

    return () => {
      channels.forEach(channel => {
        void supabase.removeChannel(channel);
      });
    };
  }, [
    activeDoctorControlRequest?.id,
    activeDoctorControlRequest?.requesterId,
    actorUser?.id,
    exitRemoteDoctorControlSession,
    isAdminInActiveControlMode,
  ]);

  const handleApproveDoctorControl = React.useCallback(async () => {
    if (!pendingDoctorControlRequest) return;

    try {
      await approveDoctorControlMutation.mutateAsync(pendingDoctorControlRequest.id);
      toast.success(
        t({
          id: 'shared.layout.AppShell.doctor-control-approved',
          message: 'Access approved',
        })
      );
      setDoctorApprovalDialogOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t({
              id: 'shared.layout.AppShell.unable-to-approve-request',
              message: 'Unable to approve request',
            })
      );
    }
  }, [approveDoctorControlMutation, pendingDoctorControlRequest]);

  const handleDenyDoctorControl = React.useCallback(async () => {
    if (!pendingDoctorControlRequest) return;

    try {
      await denyDoctorControlMutation.mutateAsync(pendingDoctorControlRequest.id);
      toast.success(
        t({
          id: 'shared.layout.AppShell.doctor-control-denied',
          message: 'Access denied',
        })
      );
      setDoctorApprovalDialogOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t({
              id: 'shared.layout.AppShell.unable-to-deny-request',
              message: 'Unable to deny request',
            })
      );
    }
  }, [denyDoctorControlMutation, pendingDoctorControlRequest]);

  const handleRevokeDoctorControl = React.useCallback(async () => {
    if (!activeDoctorControlSession) return;

    try {
      await revokeDoctorControlMutation.mutateAsync(activeDoctorControlSession.id);
      toast.success(
        t({
          id: 'shared.layout.AppShell.doctor-control-revoked',
          message: 'Access revoked',
        })
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t({
              id: 'shared.layout.AppShell.unable-to-revoke-access',
              message: 'Unable to revoke access',
            })
      );
    }
  }, [activeDoctorControlSession, revokeDoctorControlMutation]);

  const handleEndDoctorControl = React.useCallback(async () => {
    if (!activeDoctorControlRequest) return;

    try {
      await endDoctorControlMutation.mutateAsync(activeDoctorControlRequest.id);
      clearDoctorControlStorage();
      queryClient.clear();
      apiClient.clearSessionBoundState('doctor_control_ended');
      await queryPersister.removeClient();
      await refetchProfile();
      toast.success(
        t({
          id: 'shared.layout.AppShell.doctor-control-ended',
          message: 'Control mode ended',
        })
      );
      navigate(returnPath, { replace: true });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t({
              id: 'shared.layout.AppShell.unable-to-end-control-mode',
              message: 'Unable to end control mode',
            })
      );
    }
  }, [activeDoctorControlRequest, endDoctorControlMutation, navigate, refetchProfile, returnPath]);

  const workspaceTogglePath = workspaceHomePath;
  const localizedWorkspaceTogglePath = localizePath(workspaceTogglePath, locale);
  const workspaceToggleLabel = isDelegatedDoctorWorkspace
    ? t({
        id: 'shared.layout.AppShell.doctor-dashboard',
        message: 'Doctor dashboard',
      })
    : t({
        id: 'shared.layout.AppShell.return-to-doctor',
        message: 'Return to doctor',
      });
  const requesterRole = String(activeDoctorControlSession?.requester.role ?? '');
  const requesterRoleLabel =
    requesterRole === 'super_admin' || requesterRole === 'superadmin'
      ? t({
          id: 'shared.layout.AppShell.role-super-admin',
          message: 'Super Admin',
        })
      : t({
          id: 'shared.layout.AppShell.role-admin',
          message: 'Admin',
        });

  const shellBanner = (
    <>
      {user?.role === 'doctor' && pendingDoctorControlRequest && (
        <Alert className='border-blue-300 bg-blue-50 text-blue-950'>
          <ShieldCheck className='h-4 w-4' />
          <AlertTitle>
            {t({
              id: 'shared.layout.AppShell.account-control-request-pending',
              message: 'Account control request pending',
            })}
          </AlertTitle>
          <AlertDescription className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <span>
              {t(
                {
                  id: 'shared.layout.AppShell.requester-needs-your-approval',
                  message: '{requesterName} wants temporary access to act on your behalf.',
                },
                {
                  requesterName: pendingDoctorControlRequest.requester.fullName,
                }
              )}
            </span>
            <Button size='sm' onClick={() => setDoctorApprovalDialogOpen(true)}>
              {t({
                id: 'shared.layout.AppShell.review-request',
                message: 'Review request',
              })}
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </>
  );

  const skipLink = (
    <a
      href='#main-content'
      className='sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground'
    >
      {t({ id: 'shared.layout.AppShell.skip-to-content', message: 'Skip to main content' })}
    </a>
  );
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    document.getElementById('main-content')?.focus({ preventScroll: true });
  }, [location.pathname]);

  const controlModeTopBar = isAdminInActiveControlMode ? (
    <div className='sticky top-0 z-50 shrink-0 border-b border-slate-800 bg-slate-950 text-slate-100'>
      <div className='flex min-h-12 items-center justify-between gap-2 px-3 py-1.5 sm:px-4 lg:px-6'>
        <div className='flex min-w-0 flex-1 items-center gap-2 sm:gap-3'>
          <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400 text-slate-950 shadow-sm sm:h-auto sm:w-auto sm:rounded-md sm:px-2.5 sm:py-1 sm:text-xs sm:font-semibold sm:uppercase sm:tracking-[0.1em]'>
            <ShieldCheck className='h-4 w-4 sm:mr-1.5 sm:h-3.5 sm:w-3.5' />
            <span className='hidden sm:inline'>
              {t({
                id: 'shared.layout.AppShell.delegated-control-mode',
                message: 'Delegated control',
              })}
            </span>
          </div>
          <div className='min-w-0 flex-1'>
            <p className='truncate text-xs font-semibold leading-tight sm:text-sm text-slate-100'>
              {t(
                {
                  id: 'shared.layout.AppShell.you-are-managing-account',
                  message: 'Managing: {doctorIdentity}',
                },
                {
                  doctorIdentity:
                    activeDoctorControlRequest.doctor.fullName ||
                    activeDoctorControlRequest.doctor.email,
                }
              )}
            </p>
            <p className='hidden truncate text-xs text-slate-400 lg:block'>
              {t(
                {
                  id: 'shared.layout.AppShell.admin-session-remains-active',
                  message: 'Your {roleLabel} session remains active.',
                },
                {
                  roleLabel:
                    actorUser?.role === 'super_admin'
                      ? t({
                          id: 'shared.layout.AppShell.role-super-admin-lowercase',
                          message: 'super admin',
                        })
                      : t({
                          id: 'shared.layout.AppShell.role-admin-lowercase',
                          message: 'admin',
                        }),
                }
              )}
            </p>
          </div>
        </div>

        <div className='flex shrink-0 items-center gap-1.5 sm:gap-2'>
          <Button
            asChild
            size='sm'
            variant='ghost'
            className='h-8 border border-slate-700 bg-slate-900 px-2.5 text-xs text-slate-100 hover:bg-slate-800 hover:text-white sm:px-3'
          >
            <Link to={localizedWorkspaceTogglePath}>
              <span className='hidden sm:inline'>{workspaceToggleLabel}</span>
              <span className='sm:hidden'>
                {t({
                  id: 'shared.layout.AppShell.back',
                  message: 'Back',
                })}
              </span>
            </Link>
          </Button>
          <Button
            size='sm'
            className='h-8 bg-brand-blue px-2.5 text-xs text-white hover:bg-brand-blue/90 sm:px-3'
            onClick={() => void handleEndDoctorControl()}
            disabled={endDoctorControlMutation.isPending}
          >
            {t({
              id: 'shared.layout.AppShell.exit-control-mode',
              message: 'Exit',
            })}
          </Button>
        </div>
      </div>
    </div>
  ) : user?.role === 'doctor' && activeDoctorControlSession ? (
    <div className='sticky top-0 z-50 shrink-0 border-b border-slate-800 bg-slate-950 text-slate-100'>
      <div className='flex min-h-12 items-center justify-between gap-2 px-3 py-1.5 sm:px-4 lg:px-6'>
        <div className='flex min-w-0 flex-1 items-center gap-2 sm:gap-3'>
          <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400 text-slate-950 shadow-sm sm:h-auto sm:w-auto sm:rounded-md sm:px-2.5 sm:py-1 sm:text-xs sm:font-semibold sm:uppercase sm:tracking-[0.1em]'>
            <ShieldCheck className='h-4 w-4 sm:mr-1.5 sm:h-3.5 sm:w-3.5' />
            <span className='hidden sm:inline'>
              {t({
                id: 'shared.layout.AppShell.account-is-controlled',
                message: 'Controlled',
              })}
            </span>
          </div>
          <div className='min-w-0 flex-1'>
            <p className='truncate text-xs font-semibold leading-tight sm:text-sm text-slate-100'>
              {t(
                {
                  id: 'shared.layout.AppShell.account-controlled-by-requester',
                  message: '{requesterName} is controlling your account',
                },
                {
                  requesterName: activeDoctorControlSession.requester.fullName,
                }
              )}
            </p>
            <div className='hidden items-center gap-1.5 lg:flex'>
              <span className='rounded border border-slate-700 bg-slate-900 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wider text-slate-200'>
                {requesterRoleLabel}
              </span>
              <span className='truncate text-xs text-slate-400'>
                {t({
                  id: 'shared.layout.AppShell.revoke-any-time',
                  message: 'You can stop this session anytime.',
                })}
              </span>
            </div>
          </div>
        </div>

        <div className='flex shrink-0 items-center'>
          <Button
            size='sm'
            className='h-8 bg-brand-blue px-2.5 text-xs text-white hover:bg-brand-blue/90 sm:px-3'
            onClick={() => void handleRevokeDoctorControl()}
            disabled={revokeDoctorControlMutation.isPending}
          >
            {t({
              id: 'shared.layout.AppShell.stop-control',
              message: 'Stop',
            })}
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  const doctorApprovalDialog = pendingDoctorControlRequest ? (
    <Dialog open={doctorApprovalDialogOpen} onOpenChange={setDoctorApprovalDialogOpen}>
      <DialogContent className='max-w-[400px] overflow-hidden rounded-2xl p-0 sm:rounded-3xl'>
        <div className='flex flex-col items-center justify-center bg-blue-50/50 px-6 py-8 text-center dark:bg-blue-950/20'>
          <div className='mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50'>
            <ShieldCheck className='h-7 w-7 text-blue-600 dark:text-blue-400' />
          </div>
          <DialogTitle className='text-xl font-bold tracking-tight'>
            {t({
              id: 'shared.layout.AppShell.review-account-control-request',
              message: 'Account Control Request',
            })}
          </DialogTitle>
          <DialogDescription className='mt-2 text-sm text-muted-foreground'>
            {t({
              id: 'shared.layout.AppShell.approval-keeps-admin-in-own-session',
              message: 'Grant temporary access to act on your behalf. You can revoke it anytime.',
            })}
          </DialogDescription>
        </div>
        <div className='px-6 py-6'>
          <div className='mb-5 flex items-center gap-3 rounded-xl border border-border/40 bg-muted/10 p-3 shadow-sm'>
            <Avatar className='h-10 w-10 border border-border/50'>
              <AvatarFallback className='bg-primary/5 text-primary text-xs font-medium'>
                {`${pendingDoctorControlRequest.requester.firstName.charAt(0)}${pendingDoctorControlRequest.requester.lastName.charAt(0)}`.trim() ||
                  t({
                    id: 'shared.layout.AppShell.initials-fallback-jm',
                    message: 'JM',
                  })}
              </AvatarFallback>
            </Avatar>
            <div className='flex flex-col'>
              <span className='text-sm font-semibold'>
                {pendingDoctorControlRequest.requester.fullName}
              </span>
              <span className='text-xs font-medium text-muted-foreground'>
                {pendingDoctorControlRequest.requester.role === 'super_admin'
                  ? t({
                      id: 'shared.layout.AppShell.requester-role-super-admin',
                      message: 'Super Admin',
                    })
                  : t({
                      id: 'shared.layout.AppShell.requester-role-admin',
                      message: 'Admin',
                    })}
              </span>
            </div>
          </div>
          <div className='mb-2'>
            <h4 className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
              {t({
                id: 'shared.layout.AppShell.request-reason',
                message: 'Reason for access',
              })}
            </h4>
          </div>
          <div className='rounded-xl bg-muted/30 p-4 text-sm leading-relaxed text-foreground shadow-inner border border-border/20 max-h-[120px] overflow-y-auto'>
            {pendingDoctorControlRequest.reason}
          </div>
        </div>
        <div className='flex flex-col gap-2 bg-muted/20 px-6 py-4 sm:flex-row sm:justify-end'>
          <Button
            variant='outline'
            className='w-full sm:w-auto'
            onClick={() => void handleDenyDoctorControl()}
            disabled={denyDoctorControlMutation.isPending || approveDoctorControlMutation.isPending}
          >
            {t({
              id: 'shared.layout.AppShell.deny-access',
              message: 'Deny',
            })}
          </Button>
          <Button
            className='w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white'
            onClick={() => void handleApproveDoctorControl()}
            disabled={denyDoctorControlMutation.isPending || approveDoctorControlMutation.isPending}
          >
            {t({
              id: 'shared.layout.AppShell.approve-access',
              message: 'Approve Access',
            })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  ) : null;

  const remoteControlExitDialog = (
    <Dialog
      open={Boolean(remoteControlExitDialogReason)}
      onOpenChange={open => {
        if (!open) setRemoteControlExitDialogReason(null);
      }}
    >
      <DialogContent className='max-w-[420px]'>
        <div className='flex flex-col gap-4'>
          <div className='flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700'>
            <ShieldCheck className='h-6 w-6' />
          </div>
          <div className='space-y-2'>
            <DialogTitle>
              {remoteControlExitDialogReason === 'revoked'
                ? t({
                    id: 'shared.layout.AppShell.doctor-revoked-access-title',
                    message: 'Doctor revoked access',
                  })
                : t({
                    id: 'shared.layout.AppShell.control-session-ended-title',
                    message: 'Control session ended',
                  })}
            </DialogTitle>
            <DialogDescription>
              {remoteControlExitDialogReason === 'revoked'
                ? t({
                    id: 'shared.layout.AppShell.doctor-revoked-access-description',
                    message:
                      'The doctor stopped account control. You have been returned to your admin session.',
                  })
                : t({
                    id: 'shared.layout.AppShell.control-session-ended-description',
                    message: 'Account control ended. You have been returned to your admin session.',
                  })}
            </DialogDescription>
          </div>
          <div className='flex justify-end'>
            <Button onClick={() => setRemoteControlExitDialogReason(null)}>
              {t({
                id: 'shared.layout.AppShell.ok',
                message: 'OK',
              })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const displayNavItems = React.useMemo(() => {
    const doctorPatientsUnread = doctorPatientsUnreadQuery.data ?? 0;
    const messagingUnread = (messagingRoomsQuery.data?.rooms ?? []).reduce(
      (total, room) => total + Math.max(0, room.unreadCountCache ?? 0),
      0
    );

    return navItems.map(item => {
      if (item.key === 'patients' && effectivePortalRole === 'doctor') {
        return { ...item, badge: doctorPatientsUnread };
      }
      if (item.key === 'messages') {
        return { ...item, badge: messagingUnread };
      }
      return item;
    });
  }, [
    doctorPatientsUnreadQuery.data,
    effectivePortalRole,
    messagingRoomsQuery.data?.rooms,
    navItems,
  ]);

  const currentNavItem = React.useMemo(() => {
    const explicit = getNavItemByPath(normalizedPathname, effectivePortalRole);
    const sourceNav = displayNavItems;
    return explicit ?? sourceNav.find(item => item.path === location.pathname);
  }, [displayNavItems, effectivePortalRole, location.pathname, normalizedPathname]);

  const shellRoutePathname = stripLocaleFromPathname(normalizedPathname);
  const isQuotesShellRoute = shellRoutePathname.startsWith('/quotes');
  const isMessagesShellRoute = shellRoutePathname.startsWith('/messages');
  const isCallsShellRoute = shellRoutePathname.startsWith('/calls');
  const isAiSalesShellRoute = shellRoutePathname.startsWith('/super-admin/ai-sales');
  const isStaffDashboardRoute =
    shellRoutePathname.replace(/\/$/, '') === '/dashboard' &&
    (effectivePortalRole === 'admin' || effectivePortalRole === 'super_admin');
  const isMessagesChatWindow =
    isMessagesShellRoute && new URLSearchParams(location.search).has('room');
  const isFullBleedShellRoute =
    isQuotesShellRoute ||
    isMessagesShellRoute ||
    isCallsShellRoute ||
    isAiSalesShellRoute ||
    shellRoutePathname.startsWith('/admin/travel-planning/');

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!user) {
    // This should not happen as AppShell is only rendered for authenticated users
    return null;
  }

  // Mobile layout for patients: floating bottom nav + compact header
  const isPatient = user.role === 'patient';
  // The call dock + per-line audio are mounted once per role layout below. The
  // three layouts are mutually exclusive (only one return runs), so this is a
  // single live mount, not duplication; a shared definition keeps them in sync.
  // GlobalCallAlert self-gates on call-staff roles, so it is safe everywhere.
  const callOverlay = (
    <>
      <CallsShellOverlay />
      <DiagnosticRecorderOverlay />
    </>
  );
  if (isPatient && isMobile) {
    return (
      <ErrorBoundary>
        <GlobalMessagingRealtimeProvider>
          <div
            className={getMobileShellClassName({
              isMessagesShellRoute,
              isMessagesChatWindow,
            })}
          >
            {skipLink}
            {controlModeTopBar}
            <NetworkStatusBanner />
            {!isMessagesShellRoute ? (
              <PatientMobileHeader key={`patient-mobile-header-${locale}`} user={user} />
            ) : null}
            <main
              id='main-content'
              tabIndex={-1}
              className={
                isMessagesShellRoute
                  ? 'flex-1 min-h-0 overflow-hidden'
                  : 'flex-1 min-h-0 px-4 pt-3 pb-[calc(6rem+env(safe-area-inset-bottom))]'
              }
            >
              {!isMessagesShellRoute ? <div className='mb-3 space-y-3'>{shellBanner}</div> : null}
              <Suspense fallback={<LoadingSkeleton />}>{children || <Outlet />}</Suspense>
            </main>
            {doctorApprovalDialog}
            {remoteControlExitDialog}
            {callOverlay}
            {shouldShowMobileBottomNav(isMessagesChatWindow) ? (
              <MobileBottomNav
                key={`patient-mobile-nav-${locale}`}
                navItems={displayNavItems}
                navKeys={MOBILE_NAV_KEYS_BY_ROLE.patient}
                role={user.role}
                disableNavPrefetch={shouldThrottleControlModeNetwork}
              />
            ) : null}
          </div>
        </GlobalMessagingRealtimeProvider>
      </ErrorBoundary>
    );
  }

  // Mobile layout for doctors: floating bottom nav + compact header
  const isDoctor = effectivePortalRole === 'doctor';
  if (isDoctor && isMobile) {
    const DOCTOR_SHORT_LABELS = {
      dashboard: t({
        id: 'shared.layout.AppShell.my-day',
        message: 'My Day',
      }),
    };

    return (
      <ErrorBoundary>
        <GlobalMessagingRealtimeProvider>
          <div
            className={getMobileShellClassName({
              isMessagesShellRoute,
              isMessagesChatWindow,
            })}
          >
            {skipLink}
            {controlModeTopBar}
            <NetworkStatusBanner />
            {!isMessagesShellRoute ? (
              <DoctorMobileHeader key={`doctor-mobile-header-${locale}`} user={user} />
            ) : null}
            <main
              id='main-content'
              tabIndex={-1}
              className={
                isMessagesShellRoute
                  ? 'flex-1 min-h-0 overflow-hidden'
                  : 'flex-1 min-h-0 p-3 pb-[calc(6rem+env(safe-area-inset-bottom))]'
              }
            >
              {!isMessagesShellRoute ? <div className='mb-3 space-y-3'>{shellBanner}</div> : null}
              <Suspense fallback={<LoadingSkeleton />}>{children || <Outlet />}</Suspense>
            </main>
            {doctorApprovalDialog}
            {remoteControlExitDialog}
            {callOverlay}
            {shouldShowMobileBottomNav(isMessagesChatWindow) ? (
              <MobileBottomNav
                key={`doctor-mobile-nav-${locale}`}
                navItems={displayNavItems}
                navKeys={MOBILE_NAV_KEYS_BY_ROLE.doctor}
                role={effectivePortalRole}
                shortLabels={DOCTOR_SHORT_LABELS}
                disableNavPrefetch={shouldThrottleControlModeNetwork}
              />
            ) : null}
          </div>
        </GlobalMessagingRealtimeProvider>
      </ErrorBoundary>
    );
  }

  // Default desktop/tablet layout with sidebar (staff on phones also get a compact bottom nav)
  const staffMobileNavKeys =
    isMobile && (effectivePortalRole === 'admin' || effectivePortalRole === 'super_admin')
      ? MOBILE_NAV_KEYS_BY_ROLE[effectivePortalRole]
      : null;
  const showStaffMobileNav =
    staffMobileNavKeys !== null && shouldShowMobileBottomNav(isMessagesChatWindow);
  return (
    <ErrorBoundary>
      <GlobalMessagingRealtimeProvider>
        <SidebarProvider>
          <div className='flex h-dvh w-full flex-col overflow-hidden bg-background'>
            {skipLink}
            {controlModeTopBar}
            <div className='flex min-h-0 flex-1 overflow-hidden'>
              <AppSidebar
                key={`sidebar-${locale}`}
                user={user}
                navItems={displayNavItems}
                roleOverride={effectivePortalRole}
                disableNavPrefetch={shouldThrottleControlModeNetwork}
              />
              <SidebarInset
                className={
                  isFullBleedShellRoute || isStaffDashboardRoute
                    ? 'flex flex-col flex-1 min-w-0 overflow-hidden md:!m-0 md:!rounded-none md:!shadow-none'
                    : 'flex flex-col flex-1 min-w-0 overflow-hidden'
                }
              >
                <NetworkStatusBanner />
                {!isMessagesShellRoute ? (
                  <AppHeader
                    key={`header-${locale}`}
                    user={user}
                    currentPage={currentNavItem}
                    roleOverride={effectivePortalRole}
                  />
                ) : null}
                <main
                  id='main-content'
                  tabIndex={-1}
                  className={`min-h-0 flex-1 min-w-0 ${
                    showStaffMobileNav ? 'pb-[calc(6rem+env(safe-area-inset-bottom))] ' : ''
                  }${
                    isFullBleedShellRoute
                      ? 'overflow-hidden'
                      : isStaffDashboardRoute
                        ? 'overflow-y-auto'
                        : 'space-y-4 p-2 sm:p-4 md:p-6 overflow-y-auto'
                  }`}
                >
                  {!isMessagesShellRoute ? <div className='space-y-3'>{shellBanner}</div> : null}
                  <Suspense fallback={<LoadingSkeleton />}>{children || <Outlet />}</Suspense>
                </main>
                {doctorApprovalDialog}
                {remoteControlExitDialog}
                {callOverlay}
              </SidebarInset>
            </div>
            {showStaffMobileNav && staffMobileNavKeys ? (
              <StaffMobileNav
                key={`staff-mobile-nav-${locale}`}
                navItems={displayNavItems}
                navKeys={staffMobileNavKeys}
                role={user.role}
                disableNavPrefetch={shouldThrottleControlModeNetwork}
              />
            ) : null}
          </div>
        </SidebarProvider>
      </GlobalMessagingRealtimeProvider>
    </ErrorBoundary>
  );
};
